import { useParams, useSearchParams } from 'react-router-dom';
import InvitationV2 from '@/invitation/render/InvitationV2';
import { toEstilo } from '@/invitation/payload/styles';
import type { InvitationPayload } from '@/invitation/payload/types';
// Fotos demo del DS (deterministas para visual regression).
import portada from '@ds/assets/photos/portada.jpg';
import parejaVertical from '@ds/assets/photos/pareja-vertical.jpg';
import novia from '@ds/assets/photos/novia.jpg';
import novio from '@ds/assets/photos/novio.jpg';
import ceremonia from '@ds/assets/photos/ceremonia.jpg';
import recepcion from '@ds/assets/photos/recepcion.jpg';
import vestimenta from '@ds/assets/photos/vestimenta.jpg';
import g1 from '@ds/assets/photos/galeria-1.jpg';
import g2 from '@ds/assets/photos/galeria-2.jpg';
import g3 from '@ds/assets/photos/galeria-3.jpg';
import g4 from '@ds/assets/photos/galeria-4.jpg';
import g5 from '@ds/assets/photos/galeria-5.jpg';
import g6 from '@ds/assets/photos/galeria-6.jpg';

/** Payload demo con todas las secciones pobladas (datos fijos → screenshots estables). */
const DEMO_PAYLOAD: Omit<InvitationPayload, 'estilo'> = {
  boda: { fecha: '2026-11-14T17:00:00' },
  novia: { nombreCorto: 'Ana', nombreCompleto: 'Ana Salas Medina' },
  novio: { nombreCorto: 'Diego', nombreCompleto: 'Diego Cordero Ríos' },
  historia: {
    texto: 'Con la bendición de Dios y de nuestros padres, hemos decidido unir nuestras vidas.',
    frase: 'Doce años, tres ciudades y un perro después, elegimos seguir eligiéndonos.',
  },
  familia: [
    { rol: 'Padre de la Novia', nombre: 'Jorge Salas Ruiz' },
    { rol: 'Madre de la Novia', nombre: 'Rosa Medina Vega' },
    { rol: 'Padre del Novio', nombre: 'Manuel Cordero Lara' },
    { rol: 'Madre del Novio', nombre: 'Elena Ríos Prado' },
  ],
  eventos: [
    { tipo: 'religiosa', hora: '17:00', sede: 'Parroquia de San Miguel', direccion: 'Av. Reforma 1204', mapaUrl: 'https://maps.google.com' },
    { tipo: 'civil', hora: '12:00', sede: 'Registro Civil', direccion: 'Plaza de la Fábrica 3', mapaUrl: 'https://maps.google.com' },
    { tipo: 'recepcion', hora: '19:30', sede: 'Hacienda Los Laureles', direccion: 'Camino Real 45', mapaUrl: 'https://maps.google.com' },
  ],
  itinerario: [
    { momento: 'ceremonia', hora: '17:00' },
    { momento: 'coctel', hora: '18:30' },
    { momento: 'cena', hora: '20:00', detalle: 'Menú de tres tiempos' },
    { momento: 'fiesta', hora: '22:00' },
    { momento: 'despedida', hora: '02:00' },
  ],
  vestimenta: { codigo: 'formal' },
  album: { hashtag: '#AnaYDiego2026' },
  regalos: {
    mensaje: '',
    tiendaUrl: 'https://mesa.example.com',
    banco: 'Banco Nacional', titular: 'Ana Salas', cuenta: '0123 4567 8910',
    moneda: 'Bs',
    sugerencias: [
      { nombre: 'Juego de sábanas de lino', precio: 480, descripcion: 'Tono arena, king size' },
      { nombre: 'Batería de cocina', precio: 950 },
    ],
  },
  rsvp: { fechaLimite: '30 de septiembre' },
  musica: {},
  fotos: {
    portada, pareja: parejaVertical, novia, novio, vestimenta,
    galeria: [g1, g2, g3, g4, g5, g6],
    sedes: { religiosa: ceremonia, civil: g5, recepcion },
  },
  interruptores: {
    retratos: 'pareja', fotosLugares: true, fotoVestimenta: true, galeria: true,
    fotosInvitados: true, ceremoniaReligiosa: true, ceremoniaCivil: true, ninosPermitidos: false,
    coloresSugeridos: false, coloresReservados: false, regalos: true, mesaRegalos: true,
    transferencia: true, transferenciaQR: false, efectivoSobre: true, listaRegalos: true,
  },
  invitado: { nombre: 'Julio Zambrana', pases: 2 },
};

/**
 * Demo público (sin auth ni BD) de la invitación v2 por estilo: /invitations/demo/:estilo
 * 011 — acepta `?bride=&groom=` para sobrescribir SOLO los nombres (lo único "real" del
 * preview del wizard); el resto del contenido es de ejemplo. Se usa como fuente del iframe
 * del modal "Vista previa" del paso Estilo (`StyleStep`).
 */
const TemplateDemo = () => {
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const bride = searchParams.get('bride')?.trim();
  const groom = searchParams.get('groom')?.trim();

  const payload: InvitationPayload = {
    ...DEMO_PAYLOAD,
    estilo: toEstilo(templateId),
    novia: {
      ...DEMO_PAYLOAD.novia,
      nombreCorto: bride || DEMO_PAYLOAD.novia.nombreCorto,
    },
    novio: {
      ...DEMO_PAYLOAD.novio,
      nombreCorto: groom || DEMO_PAYLOAD.novio.nombreCorto,
    },
  };
  return <InvitationV2 payload={payload} open />;
};

export default TemplateDemo;
